/**
 * Cell Visibility Logic evaluator.
 *
 * A table/collection column can carry `cellsHidden` — a boolean that sets the initial visibility
 * baseline for the column's cells — and `cellVisibilityLogic`, a per-cell rule with the same
 * `{ action, eval, conditions }` shape as Conditional Logic, resolved per row (against the row's
 * sibling cells and the document fields). This mirrors how a field's `hidden` seeds its
 * `conditionalLogic` (see `customApplyLogic` in ./collectionHelper):
 *
 *   - `cellsHidden` is the initial source of truth (true -> hidden by default, false -> visible)
 *   - action 'show' reveals cells when conditions are met -> hidden = !passed && cellsHidden
 *   - action 'hide' hides cells when conditions are met   -> hidden = passed || cellsHidden
 *   - no valid rule -> the cell keeps its `cellsHidden` baseline
 *
 * Conditions may reference a document field (`condition.field`, resolved against docFields) or a
 * sibling cell in the same row (`condition.column`, resolved against rowCells). We reuse the exact
 * condition engine used by Conditional Required Logic so operators and reference resolution match
 * the rest of the app.
 */

import ConditionalLogic from '../constants/ConditionalLogic';
import { evaluateConditions } from './requiredLogic';

const { actions } = ConditionalLogic; // { show, hide }

/**
 * Whether a column carries a valid `cellVisibilityLogic` rule.
 *
 * @param {Object} column - the table column definition
 * @returns {Boolean} true when the column has a well-formed `{ action, eval, conditions[>0] }` rule
 */
export const hasCellVisibilityLogic = (column) => {
  const logic = column && column.cellVisibilityLogic;

  return !!(
    logic &&
    logic.action &&
    logic.eval &&
    Array.isArray(logic.conditions) &&
    logic.conditions.length > 0
  );
};

/**
 * Resolve whether a column's cell is hidden for a given row.
 *
 * @param {Object} column - the table column definition (may carry `cellsHidden` / `cellVisibilityLogic`)
 * @param {Object} rowCells - the row's cells map (for sibling `column` conditions)
 * @param {Array} docFields - all document fields (for `field` conditions)
 * @returns {Boolean} true when the cell should be hidden
 */
export const resolveColumnCellHidden = (column, rowCells = {}, docFields = []) => {
  const initialHidden = !!(column && column.cellsHidden);

  if (!hasCellVisibilityLogic(column)) return initialHidden;

  const logic = column.cellVisibilityLogic;
  const passed = evaluateConditions(logic.conditions, logic.eval, rowCells, docFields);

  if (logic.action === actions.show) return !passed && initialHidden;
  if (logic.action === actions.hide) return passed || initialHidden;

  return initialHidden;
};

export default {
  hasCellVisibilityLogic,
  resolveColumnCellHidden,
};
